import { constantRoutes, asyncRoutes } from '@/router'
import Layout from '@/layout'

// 将后端返回的菜单数据转换为路由格式
function filterAsyncRoutes(asyncRoutes, roles) {
  const res = []

  // 确保asyncRoutes是数组
  if (!Array.isArray(asyncRoutes)) {
    console.warn('asyncRoutes不是数组');
    return res;
  }

  asyncRoutes.forEach(route => {
    // 创建路由的副本，避免修改原始对象
    const tmp = { ...route }
    
    // 检查用户是否有该路由的权限
    if (hasPermission(roles, tmp)) {
      // 处理组件
      if (tmp.component === 'Layout') {
        tmp.component = Layout
      } else if (typeof tmp.component === 'string') {
        // 动态加载组件
        tmp.component = loadView(tmp.component)
      } else if (tmp.component === undefined) {
        // 如果组件未定义，使用404页面
        console.warn('路由组件未定义:', tmp.path);
        tmp.component = () => import('@/views/error-page/404')
      }

      // 递归处理子路由
      if (tmp.children) {
        const filteredChildren = filterAsyncRoutes(tmp.children, roles);
        
        // 只有当子路由有内容时才设置
        if (filteredChildren.length > 0) {
          tmp.children = filteredChildren;
        } else {
          // 如果没有子路由，则删除children属性
          delete tmp.children;
        }
      }
      
      res.push(tmp)
    }
  })

  return res
}

// 检查用户是否有该路由的权限
function hasPermission(roles, route) {
  // 确保roles是数组
  if (!Array.isArray(roles)) {
    console.warn('roles不是数组');
    return false;
  }
  
  // 如果用户是管理员，拥有所有权限
  if (roles.includes('admin')) {
    return true;
  }
  
  // 如果路由没有meta属性或没有权限控制，默认所有人可访问
  if (!route.meta || (!route.meta.roles && !route.meta.permission)) {
    return true;
  }
  
  // 如果路由定义了角色要求
  if (route.meta.roles && Array.isArray(route.meta.roles)) {
    return roles.some(role => route.meta.roles.includes(role));
  }
  
  // 如果路由定义了权限要求
  if (route.meta.permission) {
    // 简单起见，我们假设权限字符串中包含角色名称
    return roles.some(role => {
      if (typeof route.meta.permission === 'string') {
        return route.meta.permission.includes(role);
      }
      return false;
    });
  }
  
  return false;
}

// 将后端返回的权限列表转换为路由格式
function generateRoutesFromPermissions(permissions) {
  const res = []
  
  if (!Array.isArray(permissions) || permissions.length === 0) {
    console.warn('权限列表为空或不是数组');
    return res;
  }
  
  // 打印原始权限列表，便于调试
  console.log('原始权限列表:', JSON.stringify(permissions, null, 2));
  
  // 按照treeCode排序
  const sortedPermissions = [...permissions].sort((a, b) => {
    return a.treeCode.localeCompare(b.treeCode);
  });
  
  console.log('排序后的权限列表:', JSON.stringify(sortedPermissions, null, 2));
  
  // 构建路由树
  const permissionMap = {};
  
  // 第一步：将所有权限放入map中
  sortedPermissions.forEach(permission => {
    // 确保每个权限都有唯一的ID
    if (permission.id) {
      permissionMap[permission.id] = {
        ...permission,
        children: []
      };
    }
  });
  
  // 创建一个映射，用于存储treeCode到权限对象的映射
  const treeCodeMap = {};
  sortedPermissions.forEach(permission => {
    if (permission.treeCode) {
      treeCodeMap[permission.treeCode] = permissionMap[permission.id];
    }
  });
  
  console.log('treeCodeMap:', Object.keys(treeCodeMap));
  
  // 第二步：构建权限树
  const rootPermissions = [];
  
  sortedPermissions.forEach(permission => {
    const treeCode = permission.treeCode;
    
    // 检查是否为根节点（长度为3的treeCode）
    if (treeCode && treeCode.length === 3) {
      console.log(`找到根节点: ${permission.name}, treeCode: ${treeCode}`);
      rootPermissions.push(permissionMap[permission.id]);
    } else if (treeCode && treeCode.length > 3) {
      // 查找父节点
      const parentTreeCode = treeCode.substring(0, treeCode.length - 3);
      console.log(`查找 ${permission.name} 的父节点, 父treeCode: ${parentTreeCode}`);
      
      if (treeCodeMap[parentTreeCode]) {
        console.log(`将 ${permission.name} 添加为 ${treeCodeMap[parentTreeCode].name} 的子节点`);
        treeCodeMap[parentTreeCode].children.push(permissionMap[permission.id]);
      } else {
        console.warn(`未找到菜单 ${permission.name}(${treeCode}) 的父节点`);
      }
    }
  });
  
  console.log('构建的权限树:', JSON.stringify(rootPermissions, null, 2));
  
  // 第三步：将权限树转换为路由配置
  rootPermissions.forEach(rootPermission => {
    // 检查是否是"每日健康填报"菜单
    const isHealthReport = rootPermission.name.includes('每日健康填报');
    
    // 如果是"每日健康填报"菜单，或者根节点只有一个子节点且根节点没有自己的路径，直接使用子节点作为路由
    if ((isHealthReport && rootPermission.path) || 
        (rootPermission.children && 
         rootPermission.children.length === 1 && 
         !rootPermission.path && 
         rootPermission.children[0].path)) {
      
      // 如果是"每日健康填报"菜单且有自己的路径
      if (isHealthReport && rootPermission.path) {
        console.log(`${rootPermission.name} 是健康填报菜单，直接作为一级菜单`);
        
        const route = {
          path: getPathFromComponent(rootPermission.path),
          component: Layout,
          name: rootPermission.name,
          meta: {
            title: rootPermission.name,
            icon: getIcon(rootPermission.name, rootPermission.icon)
          },
          children: [{
            path: '',
            name: rootPermission.name + '-index',
            component: loadView(rootPermission.path),
            meta: {
              title: rootPermission.name,
              icon: getIcon(rootPermission.name, rootPermission.icon)
            }
          }]
        };
        
        res.push(route);
      } 
      // 如果根节点只有一个子节点且根节点没有自己的路径
      else if (rootPermission.children && rootPermission.children.length === 1) {
        const child = rootPermission.children[0];
        console.log(`${rootPermission.name} 只有一个子菜单 ${child.name}，直接使用子菜单作为路由`);
        
        const route = {
          path: getPathFromComponent(child.path),
          component: Layout,
          name: child.name,
          meta: {
            title: child.name,
            icon: getIcon(child.name, child.icon)
          },
          children: [{
            path: '',
            name: child.name + '-index',
            component: loadView(child.path),
            meta: {
              title: child.name,
              icon: getIcon(child.name, child.icon)
            }
          }]
        };
        
        res.push(route);
      }
    } else {
      // 创建根路由
      const route = {
        path: rootPermission.path ? getPathFromComponent(rootPermission.path) : `/${getRoutePath(rootPermission.name)}`,
        component: Layout,
        name: rootPermission.name,
        meta: {
          title: rootPermission.name,
          icon: getIcon(rootPermission.name, rootPermission.icon),
          isParent: rootPermission.children && rootPermission.children.length > 0 // 只有当有子菜单时才标记为父级菜单
        },
        children: []
      };
      
      console.log(`创建根路由: ${rootPermission.name}, 路径: ${route.path}`);
      
      // 处理子路由
      if (rootPermission.children && rootPermission.children.length > 0) {
        rootPermission.children.forEach(child => {
          // 创建子路由
          const childRoute = {
            path: child.path ? getLastPathSegment(child.path) : getRoutePath(child.name),
            name: child.name,
            component: loadView(child.path),
            meta: {
              title: child.name,
              icon: getIcon(child.name, child.icon)
            }
          };
          
          // 如果子菜单还有子菜单，标记为父级菜单
          if (child.children && child.children.length > 0) {
            childRoute.meta.isParent = true;
          }
          
          console.log(`为 ${rootPermission.name} 添加子路由: ${child.name}, 路径: ${childRoute.path}, 组件路径: ${child.path}`);
          route.children.push(childRoute);
          
          // 如果子路由还有子路由，递归处理
          if (child.children && child.children.length > 0) {
            console.log(`${child.name} 有 ${child.children.length} 个子节点，递归处理`);
            processChildren(child.children, route.children);
          }
        });
      } else if (rootPermission.path) {
        // 如果没有子路由但有路径，创建一个默认子路由
        const defaultChild = {
          path: '',
          name: rootPermission.name,
          component: loadView(rootPermission.path) || (() => import('@/views/dashboard/index')),
          meta: {
            title: rootPermission.name,
            icon: getIcon(rootPermission.name, rootPermission.icon)
          }
        };
        console.log(`为 ${rootPermission.name} 创建默认子路由`);
        route.children.push(defaultChild);
      } else {
        // 如果既没有子路由也没有路径，创建一个空的子路由
        const emptyChild = {
          path: '',
          name: rootPermission.name,
          component: () => import('@/views/dashboard/index'),
          meta: {
            title: rootPermission.name,
            icon: getIcon(rootPermission.name, rootPermission.icon)
          }
        };
        console.log(`为 ${rootPermission.name} 创建空子路由`);
        route.children.push(emptyChild);
      }
      
      // 确保每个路由都有子路由，这样才能正确显示
      if (route.children.length === 0) {
        console.log(`${rootPermission.name} 没有子路由，添加一个默认子路由`);
        route.children.push({
          path: '',
          name: rootPermission.name + '-default',
          component: () => import('@/views/dashboard/index'),
          meta: {
            title: rootPermission.name,
            icon: getIcon(rootPermission.name, rootPermission.icon)
          }
        });
      }
      
      res.push(route);
    }
  });
  
  console.log('生成的路由配置:', JSON.stringify(res, null, 2));
  return res;
}

// 从组件路径中提取路由路径
function getPathFromComponent(componentPath) {
  if (!componentPath) return '';
  
  // 移除开头的 /views/
  let path = componentPath;
  if (path.startsWith('/views/')) {
    path = path.substring(7);
  }
  
  // 移除 /index 或 /Index 结尾
  path = path.replace(/\/[Ii]ndex$/, '');
  
  // 添加前导斜杠
  if (!path.startsWith('/')) {
    path = '/' + path;
  }
  
  return path;
}

// 递归处理子路由
function processChildren(children, routeChildren) {
  children.forEach(child => {
    // 创建子路由
    let childPath = '';
    if (child.path) {
      // 从路径中提取最后一部分作为子路由路径
      const pathParts = child.path.replace('/views', '').replace('/Index', '').split('/');
      // 如果路径是 /views/system/user/Index，则提取 user 作为路径
      childPath = pathParts[pathParts.length - 1] || pathParts[pathParts.length - 2] || child.id.toString();
      
      // 确保路径不为空
      if (!childPath) {
        childPath = child.id.toString();
      }
    } else {
      childPath = child.id.toString();
    }
    
    console.log(`处理子路由: ${child.name}, 路径: ${childPath}, 原始路径: ${child.path}`);
    
    const childRoute = {
      path: childPath,
      name: child.name,
      component: loadView(child.path),
      meta: {
        title: child.name,
        icon: getIcon(child.name, child.icon)
      }
    };
    
    // 如果还有子节点，递归处理
    if (child.children && child.children.length > 0) {
      childRoute.meta.isParent = true; // 标记为父级菜单
      childRoute.children = [];
      console.log(`${child.name} 有 ${child.children.length} 个子节点，递归处理`);
      processChildren(child.children, childRoute.children);
    }
    
    routeChildren.push(childRoute);
  });
}

// 动态加载组件
export const loadView = (view) => {
  if (!view) {
    console.error('组件路径为空');
    return () => import('@/views/dashboard/index');
  }
  
  // 检查view是否为字符串
  if (typeof view !== 'string') {
    console.error('组件路径不是字符串:', view);
    return () => import('@/views/dashboard/index');
  }
  
  console.log('原始组件路径:', view);
  
  // 处理后端返回的路径格式
  let normalizedView = view;
  
  // 移除开头的 /views/
  if (normalizedView.startsWith('/views/')) {
    normalizedView = normalizedView.substring(7);
  }
  
  // 处理 Index 结尾的情况
  if (normalizedView.endsWith('/Index')) {
    normalizedView = normalizedView.replace('/Index', '/index');
  } else if (normalizedView.endsWith('Index')) {
    normalizedView = normalizedView.replace('Index', 'index');
  }
  
  // 规范化路径，移除开头的斜杠
  normalizedView = normalizedView.replace(/^\//, '');
  
  console.log('规范化后的组件路径:', normalizedView);
  
  try {
    // 尝试直接加载组件
    return () => import(`@/views/${normalizedView}`).catch(error => {
      console.error(`加载组件失败: @/views/${normalizedView}`, error);
      
      // 尝试加载index.vue
      if (!normalizedView.endsWith('/index') && !normalizedView.endsWith('/index.vue')) {
        console.log(`尝试加载: @/views/${normalizedView}/index`);
        return import(`@/views/${normalizedView}/index`).catch(err => {
          console.error(`加载index组件失败: @/views/${normalizedView}/index`, err);
          return import('@/views/dashboard/index');
        });
      }
      
      return import('@/views/dashboard/index');
    });
  } catch (error) {
    console.error(`加载组件异常: @/views/${normalizedView}`, error);
    return () => import('@/views/dashboard/index');
  }
}

// 获取图标
function getIcon(name, icon) {
  // 如果已经提供了图标且以 el-icon 开头，直接使用
  if (icon && icon.startsWith('el-icon-')) {
    return icon;
  }
  
  // 根据菜单名称设置默认图标
  if (name.includes('首页') || name.includes('dashboard')) {
    return 'el-icon-s-home';
  } else if (name.includes('健康台账') || name.includes('台账')) {
    return 'el-icon-document';
  } else if (name.includes('健康档案') || name.includes('档案')) {
    return 'el-icon-folder';
  } else if (name.includes('健康填报') || name.includes('填报管理')) {
    return 'el-icon-edit-outline';
  } else if (name.includes('系统管理') || name.includes('管理')) {
    return 'el-icon-s-tools';
  } else if (name.includes('用户') || name.includes('人员')) {
    return 'el-icon-user';
  } else if (name.includes('角色') || name.includes('权限')) {
    return 'el-icon-s-check';
  } else if (name.includes('设置')) {
    return 'el-icon-setting';
  } else if (name.includes('报表') || name.includes('统计')) {
    return 'el-icon-s-data';
  } else if (name.includes('班组')) {
    return 'el-icon-s-cooperation';
  } else if (name.includes('班次')) {
    return 'el-icon-time';
  } else if (name.includes('健康评级')) {
    return 'el-icon-s-flag';
  } else {
    // 默认图标
    return 'el-icon-menu';
  }
}

// 获取路由路径（将中文名称转换为拼音或英文标识符）
function getRoutePath(name) {
  // 预定义的名称映射
  const nameMap = {
    '系统首页': 'dashboard',
    '健康台账': 'health-record',
    '健康台账列表': 'health-record-list',
    '系统管理': 'system',
    '用户管理': 'user',
    '角色管理': 'role',
    '菜单管理': 'menu',
    '填报管理': 'report',
    '健康评级管理': 'health-rating',
    '班组管理': 'team',
    '班次管理': 'shift'
  };
  
  // 如果有预定义映射，使用映射
  if (nameMap[name]) {
    return nameMap[name];
  }
  
  // 否则将中文转换为拼音（简化处理，实际应用中可能需要更复杂的转换）
  // 这里简单地移除特殊字符并转为小写
  return name
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, '-');
}

// 从路径中提取最后一段作为子路由路径
function getLastPathSegment(path) {
  if (!path) return '';
  
  // 移除开头的 /views/
  let normalizedPath = path;
  if (normalizedPath.startsWith('/views/')) {
    normalizedPath = normalizedPath.substring(7);
  }
  
  // 移除 /index 或 /Index 结尾
  normalizedPath = normalizedPath.replace(/\/[Ii]ndex$/, '');
  
  // 提取最后一段路径
  const segments = normalizedPath.split('/');
  const lastSegment = segments[segments.length - 1];
  
  return lastSegment || segments[segments.length - 2] || '';
}

const state = {
  routes: [],
  addRoutes: []
}

const mutations = {
  SET_ROUTES: (state, routes) => {
    state.addRoutes = routes
    state.routes = [...constantRoutes, ...routes]
  },
  RESET_ROUTES: (state) => {
    state.routes = []
    state.addRoutes = []
  }
}

const actions = {
  // 生成路由
  generateRoutes({ commit, rootState }, roles) {
    return new Promise(resolve => {
      // 确保roles是数组
      const userRoles = Array.isArray(roles) ? roles : [];
      console.log('生成路由，角色列表:', userRoles);
      
      let accessedRoutes = [];
      
      // 检查是否有权限列表
      const permissions = rootState.user.permissions;
      console.log('用户权限列表:', permissions);
      
      if (Array.isArray(permissions) && permissions.length > 0) {
        console.log('使用后端返回的权限列表生成路由');
        // 使用后端返回的权限列表生成路由
        accessedRoutes = generateRoutesFromPermissions(permissions);
        
        // 检查生成的路由是否为空
        if (accessedRoutes.length === 0) {
          console.warn('从权限列表生成的路由为空');
        }
      } else if (userRoles.includes('admin')) {
        console.log('用户是管理员，获取所有路由');
        // 深拷贝路由配置，避免修改原始对象
        accessedRoutes = JSON.parse(JSON.stringify(asyncRoutes));
        
        // 确保路由组件正确
        accessedRoutes.forEach(route => {
          if (route.component === 'Layout') {
            route.component = Layout;
          }
          
          // 处理子路由
          if (route.children && route.children.length > 0) {
            route.children.forEach(child => {
              // 子路由不需要处理Layout组件
              if (typeof child.component === 'string') {
                child.component = loadView(child.component);
              }
            });
          }
        });
      } else {
        // 根据角色过滤路由
        console.log('根据角色过滤路由:', userRoles);
        accessedRoutes = filterAsyncRoutes(asyncRoutes, userRoles);
      }
      
      // 避免打印大量对象
      console.log('生成的路由数量:', accessedRoutes.length);
      
      commit('SET_ROUTES', accessedRoutes);
      resolve(accessedRoutes);
    });
  },
  
  // 重置路由
  resetRoutes({ commit }) {
    commit('RESET_ROUTES');
    return Promise.resolve();
  }
}

// 获取默认路由
function getDefaultRoutes() {
  // 创建一个默认的dashboard路由，与后端返回的"系统首页"保持一致
  return [{
    path: '/dashboard',
    component: Layout,
    children: [{
      path: '',
      name: 'Dashboard',
      component: () => import('@/views/dashboard/index'),
      meta: { title: '系统首页', icon: 'el-icon-s-home' }
    }]
  }];
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}